import { IntlShape } from "react-intl";

// The earliest date allowed for the time reange filters
export const MIN_DATE = new Date(2021, 0, 1);

type Format =
  | "default"
  | "monthDayYearTime"
  | "monthDDayYear"
  | "hoursMinutes12"
  | "hoursMinutes 12" // same as "hoursMinutes12" but without spaces removed
  | "monthDDayYYWithTime"
  | "towersDateTime"
  | "popupTimestamp";

const TOWERS_TIME_PART_TYPES = new Set([
  "hour",
  "minute",
  "second",
  "fractionalSecond",
  "dayPeriod",
]);

/** Towers 24h time: ICU h23 (hours 0–23), not h24 (24:00). */
export const towersIntlH23TimeOptions: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  hourCycle: "h23",
};

/** Some ICU builds ignore hourCycle and emit 24:xx; normalize to 0–23 display. */
export function normalizeTowersH23TimeText(text: string): string {
  return text.replace(/\b24:/g, "00:");
}

/** Locale-aware date+time in one Intl pass; Summit `@` between date and time segments. */
function formatIntlDateTimeWithAtSeparator(
  date: Date,
  intl: IntlShape,
  tzKey?: string,
): string {
  const parts = intl.formatDateToParts(date, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...towersIntlH23TimeOptions,
    ...(tzKey ? { timeZone: tzKey } : {}),
  });

  const timeStart = parts.findIndex((part) =>
    TOWERS_TIME_PART_TYPES.has(part.type),
  );
  if (timeStart <= 0) {
    return parts
      .map((part) => part.value)
      .join("")
      .trim();
  }

  const datePart = parts
    .slice(0, timeStart)
    .map((part) => part.value)
    .join("")
    .trim()
    .replace(/[,;\s]+$/, "");
  const timePart = normalizeTowersH23TimeText(
    parts
      .slice(timeStart)
      .map((part) => part.value)
      .join("")
      .trim()
      .replace(/^[,;\s]+/, ""),
  );

  return `${datePart} @ ${timePart}`;
}

/**
 * Custom date formatter using FormatJS/react-intl for i18n support.
 * Note: Work in progress - not all dateFormat patterns have been migrated yet.
 *
 * @param date - Date to format
 * @param format - Format pattern to use
 * @param intl - FormatJS intl instance for i18n
 * @param tzKey - Timezone identifier
 * @returns Formatted date string
 */
export const formatCustomDate = (
  date: Date,
  format: Format,
  intl: IntlShape,
  tzKey?: string,
) => {
  switch (format) {
    case "default":
      return (
        intl.formatDate(date, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          ...(tzKey && { timeZone: tzKey }),
        }) +
        " @" +
        intl
          .formatDate(date, {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
            ...(tzKey && { timeZone: tzKey }),
          })
          .toLowerCase()
          .replace(" ", "")
      );

    case "monthDayYearTime":
      return intl
        .formatDate(new Date(date), {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          ...(tzKey && { timeZone: tzKey }),
        })
        .replace(/\//g, ".")
        .replace(", ", " @")
        .toLowerCase();

    case "monthDDayYear":
      return intl.formatDate(new Date(date), {
        month: "short",
        day: "2-digit",
        year: "numeric",
        ...(tzKey && { timeZone: tzKey }),
      });

    case "hoursMinutes12":
      return intl
        .formatDate(new Date(date), {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          ...(tzKey && { timeZone: tzKey }),
        })
        .toLowerCase()
        .replace(" ", "");

    case "hoursMinutes 12":
      return intl
        .formatDate(new Date(date), {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          ...(tzKey && { timeZone: tzKey }),
        })
        .toLowerCase();

    case "monthDDayYYWithTime":
      return (
        intl.formatDate(date, {
          timeZone: tzKey,
          month: "short",
          day: "2-digit",
          year: "2-digit",
          ...(tzKey && { timeZone: tzKey }),
        }) +
        " @ " +
        intl.formatDate(date, {
          timeZone: tzKey,
          hour: "numeric",
          minute: "numeric",
          hour12: false,
          ...(tzKey && { timeZone: tzKey }),
        })
      );

    case "towersDateTime":
      return formatIntlDateTimeWithAtSeparator(date, intl, tzKey);

    case "popupTimestamp":
      return (
        intl.formatDate(date, {
          year: "2-digit",
          month: "short",
          day: "2-digit",
          ...(tzKey && { timeZone: tzKey }),
        }) +
        " @ " +
        intl
          .formatDate(date, {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
            ...(tzKey && { timeZone: tzKey }),
          })
          .toLowerCase()
      );
  }
};

/**
 * @deprecated Use `formatCustomDate()` or FormatJS/react-intl formatting
 * functions instead for proper i18n support.
 */
export const dateFormat = {
  default: "MMM dd, yyyy @h:mmaaa",
  monthDDay: "MMM dd",
  monthDayYear: "MMM d, yyyy",
  monthDDayYear: "MMM dd, yyyy",
  monthDDayYY: "MMM dd, yy",
  hoursMinutes12: "h:mmaaa",
  hoursMinutes12WithSpace: "h:mm aaa",
  timestamp: "HH:mm, MMM dd, yyyy",
  popupTimestamp: "MMM dd, yy @ h:mm aaa",
  monthDayYearTime: "MM.dd.yy @h:mm aaa",
  monthDDayYYWithTime: "MMM dd, yy @ HH:mm",
  towersDateTime: "MMM dd, yyyy @ HH:mm",
};
